import React, { ReactNode, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
} from '../';

import NavBar from '../NavBar';

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  ...props
}: HeaderProps): ReactNode {
  const { t } = useTranslation('Header');
  const [isMobile, setIsMobile] = useState(false);
  const [studyInstanceUID, setStudyInstanceUID] = useState<string | null>(null);
  const [pacienteID, setPacienteID] = useState<string | null>(null);
  const [apiData, setApiData] = useState<any>(null);
  const [alphabeticName, setAlphabeticName] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pacienteData, setPacienteData] = useState<any>(null);
  const [estudiosData, setEstudiosData] = useState<any[]>([]);
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState('Imágenes');

  const tabs = [
    { label: 'Estudios', href: '#' },
    { label: 'Imágenes', href: '#' },
  ];

  const linkURL = studyInstanceUID
    ? `https://sys.diagnosticolomas.com/generate-pdf-visor/${studyInstanceUID}`
    : '#';

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return 'Fecha no disponible';
    }
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) {
      return 'Fecha no disponible';
    }
    return `${day}/${month}/${year}`;
  };

  const handleStudyClick = (uid: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('StudyInstanceUIDs', uid);
    window.location.href = url.toString();
  };

  const handleShare = async (uid: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('StudyInstanceUIDs', uid);
    url.searchParams.set('id', '');
    const shareURL = url.toString();

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Estudio',
          text: 'Revisa este estudio:',
          url: shareURL,
        });
      } catch (err) {
        console.error('Error al compartir:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareURL);
        alert('Enlace copiado al portapapeles');
      } catch (err) {
        console.error('Error copiando enlace:', err);
      }
    }
  };

  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  const handleTabClick = (tab: { label: string; href: string }) => {
    setSelectedTab(tab.label);
    if (tab.label === 'Estudios') {
      setIsModalOpen(true);
    } else if (tab.label === 'Imágenes') {
      setIsModalOpen(false);
    }
  };

  const buttonStyles = (isSelected: boolean, isHovered: boolean) => ({
    color: isHovered || isSelected ? '#3bd8dd' : '#f6f4e6',
    background: isHovered ? '#02468e' : isSelected ? '#00051e' : 'none',
    border: `1px solid ${isHovered || isSelected ? '#3bd8dd' : 'transparent'}`,
    cursor: 'pointer',
    padding: '7px 10px',
    marginRight: '5px',
    marginLeft: '5px',
    marginTop: '0px',
    borderRadius: '3px',
    transition: 'background-color 0.3s ease, color 0.3s ease',
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(/(Mobi|Android|iPhone|iPad|iPod)/.test(navigator.userAgent));
    };

    const urlParams = new URLSearchParams(window.location.search);
    const uid = urlParams.get('StudyInstanceUIDs');
    const paciente = urlParams.get('id');

    if (uid) {
      setStudyInstanceUID(uid);
    }
    if (paciente) {
      setPacienteID(paciente);
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchInterpretacion = async () => {
      if (!studyInstanceUID) {
        return;
      }
      try {
        const response = await fetch(
          `https://app.laughing-grothendieck.74-208-159-220.plesk.page/interpretacion`
        );
        if (response.ok) {
          const data = await response.json();
          const matchingRow = data.rows?.find((row: any) => row.uid === studyInstanceUID);
          setApiData(matchingRow ? { interpretacion: matchingRow.interpretacion_medica_si } : null);
        }
      } catch (error) {
        console.error('Error fetching interpretacion:', error);
        setApiData(null);
      }
    };
    fetchInterpretacion();
  }, [studyInstanceUID]);

  useEffect(() => {
    const fetchStudyData = async () => {
      if (!studyInstanceUID) {
        setAlphabeticName(null);
        return;
      }
      try {
        const response = await fetch(
          `https://app.laughing-grothendieck.74-208-159-220.plesk.page/study`
        );
        if (!response.ok) {
          throw new Error('Error fetching study data');
        }
        const data = await response.json();
        const matchingRow = data.rows?.find((row: any) => row.study_iuid === studyInstanceUID);
        if (matchingRow) {
          setAlphabeticName(matchingRow.alphabetic_name?.replace(/\^/g, ' ') || '');
        } else {
          setAlphabeticName(null);
        }
      } catch (error) {
        console.error('Fetch error:', error);
        setAlphabeticName(null);
      }
    };
    fetchStudyData();
  }, [studyInstanceUID]);

  useEffect(() => {
    const fetchPacienteData = async () => {
      if (!studyInstanceUID) {
        setPacienteData(null);
        return;
      }
      try {
        const url = `https://app.laughing-grothendieck.74-208-159-220.plesk.page/paciente?uid=${studyInstanceUID}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Error fetching paciente data');
        }
        const data = await response.json();
        if (data.rows && data.rows.length > 0) {
          setPacienteData(data.rows[0]);
        } else {
          setPacienteData(null);
        }
      } catch (error) {
        console.error('Fetch error:', error);
        setPacienteData(null);
      }
    };
    fetchPacienteData();
  }, [studyInstanceUID]);

  useEffect(() => {
    const fetchEstudiosData = async () => {
      if (!studyInstanceUID) {
        setEstudiosData([]);
        return;
      }
      try {
        const url = new URL('https://app.laughing-grothendieck.74-208-159-220.plesk.page/estudios');
        url.searchParams.set('uid', studyInstanceUID);
        if (pacienteID) {
          url.searchParams.set('id', pacienteID);
        }

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Error fetching estudios data');
        }
        const data = await response.json();
        if (data.rows && data.rows.length > 0) {
          setEstudiosData(data.rows);
        } else {
          setEstudiosData([]);
        }
      } catch (error) {
        console.error('Fetch error:', error);
        setEstudiosData([]);
      }
    };
    fetchEstudiosData();
  }, [studyInstanceUID, pacienteID]);

  return (
    <>
      <style>
        {`
          .scroll-desktop {
            overflow-y: scroll !important;
            scrollbar-color: #8e9da1 transparent !important;
            scrollbar-width: thin !important;
          }
          .no-scroll {
            overflow-y: hidden !important;
          }
        `}
      </style>

      <NavBar
        isSticky={isSticky}
        {...props}
      >
        <div className="relative h-[48px] items-center">
          <div className="absolute left-0 top-1/2 flex -translate-y-1/2 items-center">
            <div
              className={classNames(
                'mr-3 inline-flex items-center',
                isReturnEnabled && 'cursor-pointer'
              )}
              onClick={onClickReturn}
              data-cy="return-to-work-list"
            >
              {isReturnEnabled && <Icons.ArrowLeft className="text-primary ml-1 h-7 w-7" />}
              <div className="ml-1">
                {WhiteLabeling?.createLogoComponentFn?.(React, props) ||
                  (isMobile ? <Icons.Logo2 /> : <Icons.Logo1 />)}
              </div>
            </div>
          </div>

          {/* <div className="absolute top-1/2 left-[250px] h-8 -translate-y-1/2">{Secondary}</div> */}

          <div
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
            style={{ left: isMobile ? '53%' : '50%' }}
          >
            <div className="flex items-center justify-center space-x-1">
              {tabs.map((tab, index) => (
                <button
                  key={index}
                  style={buttonStyles(selectedTab === tab.label, hoveredTab === tab.label)}
                  onClick={() => handleTabClick(tab)}
                  onMouseEnter={() => setHoveredTab(tab.label)}
                  onMouseLeave={() => setHoveredTab(null)}
                >
                  {tab.label}
                </button>
              ))}
              {apiData?.interpretacion ? (
                <a
                  href={linkURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={buttonStyles(
                    selectedTab === 'Interpretación',
                    hoveredTab === 'Interpretación'
                  )}
                  onMouseEnter={() => setHoveredTab('Interpretación')}
                  onMouseLeave={() => setHoveredTab(null)}
                >
                  Interpretación
                </a>
              ) : (
                <div className="flex flex-col items-center">
                  <span className="text-gray-500">Interpretación</span>
                  <span className="text-gray-500">Pendiente</span>
                </div>
              )}
              {children}
            </div>
          </div>
        </div>
      </NavBar>

      {isModalOpen && (
        <div
          className="bg-secondary-dark fixed inset-0 z-[1100] bg-opacity-75"
          style={{
            top: '48px',
            left: 0,
            width: '100%',
            height: 'calc(100% - 48px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            boxSizing: 'border-box',
          }}
        >
          <div
            className={`mt-0 w-full bg-black p-5 shadow-lg ${isMobile ? 'scroll-desktop' : 'scroll-desktop'}`}
            style={{
              height: '100%',
              marginTop: 0,
              padding: '20px',
              boxSizing: 'border-box',
            }}
          >
            <div className="mb-5">
              <p className="my-1.5 text-sm text-gray-400">
                {pacienteData?.ciudad || 'Ciudad no disponible'}
              </p>
              <h1 className="m-0 text-2xl text-white">
                {alphabeticName || 'Nombre no disponible'}
              </h1>
              {pacienteData && (
                <p className="my-1.5 text-sm text-gray-400">
                  {formatDate(pacienteData.fecha_nacimiento)} •
                  {pacienteData.edad ? ` ${pacienteData.edad} Años` : ''} •{' '}
                  {pacienteData.sexo === 'Hombre'
                    ? 'Masculino'
                    : pacienteData.sexo === 'Mujer'
                      ? 'Femenino'
                      : 'Otro'}
                </p>
              )}
            </div>

            {estudiosData.map((estudioItem, index) => (
              <div
                key={index}
                role="button"
                tabIndex={0}
                onClick={() => handleStudyClick(estudioItem.uid)}
                onKeyPress={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleStudyClick(estudioItem.uid);
                  }
                }}
                className="mb-2.5 flex cursor-pointer items-center justify-between rounded-lg border border-[#1a2b3c] bg-[#0d1b2a] p-2.5 hover:bg-[#1a2b3c]"
              >
                <div className="max-w-[90%]">
                  <h2 className="m-0 text-lg text-white">{estudioItem.estudio}</h2>
                  <p className="my-1.5 flex items-center text-sm text-gray-400">
                    <i className="fas fa-procedures mr-2"></i>
                    {estudioItem.sub_estudio ? `${estudioItem.sub_estudio}` : ''}
                  </p>
                  <p className="my-1.5 flex items-center text-sm text-gray-400">
                    <i className="fas fa-calendar-alt mr-2"></i>
                    {formatDate(estudioItem.fecha_registro)}
                  </p>
                </div>
                <button
                  name="compartirBTN"
                  onClick={e => {
                    e.stopPropagation();
                    handleShare(estudioItem.uid);
                  }}
                >
                  <Icons.Compartir style={{ width: '30px', height: '30px' }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default Header;
